import React, {useState, useRef, useEffect} from "react"
import {get, includes, isFinite} from "lodash"
import className from "classnames"
import * as queryString from "query-string"
import MobileDetect from "mobile-detect"

import {axios, tt} from "@lib/helpers"

import {formDataType} from "types/pointPurchase"

import DefaultCardLink from "components/creditCard/DefaultCardLink"
import {loadPayments} from "@payjp/payments-js"

type defaultPaymentMethodCardType = {
  brand: string
  last4: string
  img: string
}

type User = {
  email: string
  phone_number: string
}

type AppPayjpPurchaseRecordProps = {
  data: {
    api_key: string
    section: Map<string, string>
    dictionary: Map<string, string>
    t: any
    user_has_points: number
    point_rate: number
    recording_fee: number
    point_purchases_path: string
    return_url: string
    payments_path: string
    create_payment_flow_path: string
    purchase_recording_booking_path: string
    default_payment_method_card: defaultPaymentMethodCardType
    user: User
  }
}
const AppPayjpPurchaseRecord = ({data}: AppPayjpPurchaseRecordProps) => {
  const {
    api_key,
    section,
    dictionary,
    t,
    user_has_points,
    point_rate,
    recording_fee,
    point_purchases_path,
    return_url,
    payments_path,
    create_payment_flow_path,
    purchase_recording_booking_path,
    default_payment_method_card,
    user,
  } = data
  const pointDiff = user_has_points - recording_fee
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasErrors, setHasErrors] = useState<boolean>(false)
  const paypalRef = React.useRef(null)
  const [payments, setPayments] = useState<any>(null)
  const [widgets, setWidgets] = useState<any>(null)
  const [clientSecret, setClientSecret] = useState<string>("")
  const [paymentFlowId, setPaymentFlowId] = useState<string>("")
  const [message, setMessage] = useState<string>("")
  const paymentFormRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // @ts-ignore
    if (typeof paypal != "undefined" && paypal) {
      if (window.paypalButton) {
        window.paypalButton.close()
      }
      // @ts-ignore
      window.paypalButton = paypal.Buttons({
        style: {
          height: 55,
        },
        createOrder: () => {
          return axios
            .post("/paypal_payments", {
              point_to_purchase: Math.abs(pointDiff),
            })
            .then((res: any) => {
              return res?.data?.data?.order_id
            })
            .catch(() => {
              window.location.reload()
            })
        },
        onApprove: (data: any) => {
          setIsLoading(true)
          axios
            .post("/paypal_payments/capture", {
              order_id: data?.orderID,
            })
            .finally(() => {
              window.location.reload()
            })
        },
      })
      window.paypalButton.render(paypalRef.current)
    }
  }, [pointDiff])

  const book = () => {
    axios.post(purchase_recording_booking_path).finally(() => {
      window.location.reload()
    })
  }
  const purchasePoint = (path: string, formData: formDataType) => {
    setIsLoading(true)
    axios
      .post(path, formData)
      .then((res2) => {
        const id = get(res2, "data.point_purchase_id")

        const poller = () => {
          axios.get(`/point_purchases/${id}`).then((res3) => {
            if (includes(["success"], get(res3, "data.data.status"))) {
              book()
            } else if (includes(["error"], get(res3, "data.data.status"))) {
              window.location.reload()
            } else if (
              includes(["requires_action"], get(res3, "data.data.status"))
            ) {
              const clientSecret = res3?.data?.data?.payjp_client_secret
              if (clientSecret && payments) {
                payments
                  .handleNextAction({clientSecret})
                  .then((result: any) => {
                    book()
                  })
                  .catch((err: any) => {
                    window.location.reload()
                  })
              }
            } else {
              window.setTimeout(() => {
                poller()
              }, 1000)
            }
          })
        }

        poller()
      })
      .catch((error: any) => {
        window.location.reload()
      })
  }
  const purchasePointByDefault = () => {
    purchasePoint(point_purchases_path, defaultCardFormData)
  }
  const defaultCardFormData: formDataType = {
    point_purchase: {
      point_to_purchase: Math.abs(pointDiff),
      return_url: return_url,
    },
  }

  const createOrUpdatePaymentFlow = async (
    point_to_purchase: string | undefined
  ) => {
    try {
      setIsLoading(true)
      const res = await axios.post(create_payment_flow_path, {
        point_to_purchase: Math.abs(pointDiff),
        payment_flow_id: paymentFlowId || null, // 2回目以降はIDを送信
      })

      setClientSecret(res.data.client_secret)
      setPaymentFlowId(res.data.payment_flow_id)

      return res.data.clientSecret
    } catch (error) {
      console.error(error)
      setMessage("決済準備に失敗しました")
      return null
    } finally {
      setIsLoading(false)
    }
  }

  // 金額変更時に実行
  useEffect(() => {
    if (pointDiff >= 0 || default_payment_method_card) {
      return
    }
    createOrUpdatePaymentFlow(Math.abs(pointDiff))
  }, [pointDiff])

  // Payments.js 初期化
  useEffect(() => {
    loadPayments(api_key, {locale: "ja"}).then(setPayments)
  }, [api_key])

  // フォームマウント
  useEffect(() => {
    if (!payments || !clientSecret) return

    let mountedForm: any = null

    const mountForm = async () => {
      const widgetInstance = payments.widgets({clientSecret})
      setWidgets(widgetInstance)

      mountedForm = widgetInstance.createForm("payment", {
        layout: "tab",
        paymentMethodOrder: ["card"],
        fields: {
          billingDetails: {
            name: "auto",
            email: "never",
            phone: "never",
          },
        },
      })

      mountedForm.mount(paymentFormRef.current)
    }

    mountForm()

    return () => mountedForm?.unmount()
  }, [payments, clientSecret])

  // 確定ボタン
  const handleConfirm = async () => {
    if (!widgets) return

    setIsLoading(true)
    setMessage("")

    try {
      const result = await widgets.confirmPayment({
        return_url: return_url,
        payment_method_data: {
          billing_details: {
            email: user.email,
            phone: user.phone_number,
          },
        },
      })

      if (result.error) {
        setMessage(`エラー: ${result.error.message}`)
      } else {
        setMessage("支払い処理が開始されました")
      }
    } catch (e: any) {
      setMessage("処理中にエラーが発生しました")
      console.log(e)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <React.Fragment>
      <div className="AppPurchaseRecord__required-points">
        <p className="AppPurchaseRecord__required-points__p">
          {get(t, "current.required_points.p")}
        </p>

        <span className="AppPurchaseRecord__required-points__span">
          {recording_fee}pt
        </span>
      </div>
      {pointDiff >= 0 ? (
        <form
          className="AppPurchaseRecord__buttons"
          onSubmit={(e) => {
            e.preventDefault()

            setIsLoading(true)
            axios.post(purchase_recording_booking_path).finally((e) => {
              window.location.reload()
            })
          }}
        >
          <button className="Button Button--large" disabled={isLoading}>
            {isLoading
              ? get(t, "dictionary.loading")
              : get(t, "current.submit")}
          </button>
        </form>
      ) : (
        <div className="AppPurchaseRecord__point">
          <h3 className="AppPurchaseRecord__point__h3">
            {get(t, "current.point.h3")}
          </h3>

          <p className="AppPurchaseRecord__point__p">
            {tt(t, "current.point.p", {point: user_has_points})}
          </p>

          <p className="AppInput">
            {Math.abs(pointDiff)}pt: {Math.abs(pointDiff * point_rate)}
            {get(t, "current.point.yen")}
          </p>

          <div className="AppBookingsReserve__buttons__row">
            <div ref={paypalRef} className="AppBookingsReserve__button"></div>
          </div>

          <h3 className="AppPurchaseRecord__point__h3">
            {get(t, "current.point.payment_method")}
          </h3>

          {default_payment_method_card ? (
            <form
              className={className("AppForm", {"AppForm--overlay": isLoading})}
              onSubmit={(e) => {
                e.preventDefault()
                purchasePointByDefault()
              }}
            >
              <DefaultCardLink
                path={payments_path}
                img={get(default_payment_method_card, "img")}
                last4={get(default_payment_method_card, "last4")}
                brand={get(default_payment_method_card, "brand")}
              />

              <div className="AppForm__row">
                <button className="Button Button--large" disabled={isLoading}>
                  {isLoading
                    ? get(dictionary, "loading")
                    : get(t, "current.submit")}
                </button>
              </div>
            </form>
          ) : (
            <form
              className={className("AppForm", {
                "AppForm--overlay": isLoading,
              })}
              onSubmit={(e) => {
                e.preventDefault()

                setIsLoading(true)
                handleConfirm()
              }}
            >
              <React.Fragment>
                <div
                  ref={paymentFormRef}
                  style={{
                    width: "100%",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    minHeight: "360px",
                    overflow: "hidden",
                    padding: "24px",
                  }}
                />
                {message && <p style={{marginTop: "16px"}}>{message}</p>}
              </React.Fragment>
              <div className="AppForm__row">
                <button
                  className="Button Button--large"
                  disabled={isLoading || hasErrors}
                >
                  {isLoading
                    ? get(dictionary, "loading")
                    : get(t, "current.submit")}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </React.Fragment>
  )
}

export default AppPayjpPurchaseRecord
